import * as i0 from '@angular/core';
import { Injectable, ApplicationRef, Injector, Inject, ErrorHandler, Component, TemplateRef, ViewContainerRef, ViewChild, EventEmitter, Directive, Input, Output, HostBinding, APP_INITIALIZER, NgModule } from '@angular/core';
import * as i2$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/router';
import { of, forkJoin, throwError, from, Subject, fromEvent, merge, timer } from 'rxjs';
import { tap, map, catchError, finalize, take, throttleTime, switchMap, concatMap } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import { HttpErrorResponse, HttpContextToken, HttpContext, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i1$1 from '@ng-bootstrap/ng-bootstrap';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import * as i2$2 from '@fortawesome/angular-fontawesome';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTimes, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import * as i1$2 from '@surescripts/left-navigation';
import * as i4 from '@angular/platform-browser';

/**
 * Singleton service for showing standardized loading spinner.
 */
class SpinnerService {
    constructor() {
        this.isLoading = false;
        this.count = 0;
    }
    show() {
        this.count++;
        this.isLoading = true;
    }
    hide(force = false) {
        if (force) {
            this.count = 0;
        }
        else {
            this.count = Math.max(this.count - 1, 0);
        }
        this.isLoading = this.count > 0;
    }
    static { this.ɵfac = function SpinnerService_Factory(t) { return new (t || SpinnerService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: SpinnerService, factory: SpinnerService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SpinnerService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], null, null); })();

/**
 * Singleton service for showing different types of Toast Messages.
 */
class ToastService {
    constructor(injector) {
        this.injector = injector;
        this.toasts = [];
    }
    message(text, options) {
        this.show(text, options);
    }
    success(text, options) {
        options = Object.assign({ classname: 'bg-success text-light', delay: 10000 }, options);
        this.show(text, options);
    }
    error(text, options) {
        options = Object.assign({ classname: 'bg-danger text-light', delay: 15000 }, options);
        this.show(text, options);
    }
    show(text, options = {}) {
        this.toasts.push({ text, ...options });
    }
    remove(toast) {
        this.toasts = this.toasts.filter(t => t !== toast);
        if (toast.global) {
            this.injector.get(ApplicationRef).tick();
        }
    }
    static { this.ɵfac = function ToastService_Factory(t) { return new (t || ToastService)(i0.ɵɵinject(Injector)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ToastService, factory: ToastService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ToastService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], () => [{ type: i0.Injector, decorators: [{
                type: Inject,
                args: [Injector]
            }] }], null); })();

class CachingService {
    constructor(http) {
        this.http = http;
        this._cache = {};
    }
    getCached(endpoint, key, cacheSeconds = 300) {
        const cachedItem = this._cache[key];
        if (cachedItem && cachedItem.expirationDate > new Date()) {
            return of(cachedItem.value);
        }
        this.bustCache(key);
        return this.http.get(endpoint).pipe(tap((resp) => {
            const newCacheItem = new CachedItem();
            newCacheItem.value = resp;
            const expirationDate = new Date();
            expirationDate.setSeconds(new Date().getSeconds() + cacheSeconds);
            newCacheItem.expirationDate = expirationDate;
            this._cache[key] = newCacheItem;
        }));
    }
    bustCache(key) {
        if (key) {
            delete this._cache[key];
        }
        else {
            this._cache = {};
        }
    }
    static { this.ɵfac = function CachingService_Factory(t) { return new (t || CachingService)(i0.ɵɵinject(i1.HttpClient)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: CachingService, factory: CachingService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CachingService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], () => [{ type: i1.HttpClient }], null); })();
class CachedItem {
    constructor(init) {
        Object.assign(this, init);
    }
}

class AuthorizationService extends CachingService {
    /**
     * Get the current user.
     * @returns {Observable<User>}
     */
    getUser() {
        return this.http.get('app/user');
    }
    /**
     * Get the current users permissions. This will be loaded into a cache and be cached for 5 minutes.
     * @returns {Array<string>}
     */
    getUserPermissions() {
        return this.getCached(`app/user/permissions`, 'user-permissions');
    }
    /**
     * Convenience method to bust the user-permissions cache before 5 minutes.
     * @param key 'user-permissions'
     */
    bustCache(key) {
        super.bustCache(key);
    }
    /**
     * Check if the user has a specific permission.
     * @param activity permission guid.
     */
    allowActivity(activity) {
        return this.getUserPermissions().pipe(map((userPermissions) => {
            return userPermissions.indexOf(activity) > -1;
        }));
    }
    /**
     * Check if the user has at least one permission.
     * @param activities array of permissions.
     */
    allowAnyActivity(activities) {
        return this.getUserPermissions().pipe(map((userPermissions) => {
            return activities.some(x => userPermissions.indexOf(x) > -1);
        }));
    }
    /**
     * Check if the user has all permissions.
     * @param activities array of permissions.
     */
    allowAllActivities(activities) {
        return this.getUserPermissions().pipe(map((userPermissions) => {
            return activities.every(x => userPermissions.indexOf(x) > -1);
        }));
    }
    static { this.ɵfac = /*@__PURE__*/ (() => { let ɵAuthorizationService_BaseFactory; return function AuthorizationService_Factory(t) { return (ɵAuthorizationService_BaseFactory || (ɵAuthorizationService_BaseFactory = i0.ɵɵgetInheritedFactory(AuthorizationService)))(t || AuthorizationService); }; })(); }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AuthorizationService, factory: AuthorizationService.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthorizationService, [{
        type: Injectable
    }], null, null); })();

class CanActivateAuth {
    constructor(init) {
        Object.assign(this, init);
    }
}

/**
 * Authorization Guard that is useful for protecting specific application routes.
 ** Using the data argument off of a Route, you can pass in a single permission.
 ** Using the data argument off of a Route, you can pass in an instance of {@link CanActivateAuth}.
 *
 * @example
 * { path: 'home', [AuthGuard], component: HomeComponent, data: { auth: AppPermissions.SiteAccess } },
 * { path: 'foo', [AuthGuard], component: FooComponent, data: { auth: { all: [AppPermissions.SiteAccess] } } }
 *
 * To apply a guard to all routes, add a parent route.
 * @example
 * const routes: Routes = [
 *   { path: 'forbidden', component: ForbiddenComponent },
 *   { path: '', canActivate: [AuthGuard], data: { auth: AppPermissions.SiteAccess }, children: [
 *     { path: 'bulkupdates', loadChildren: () => import('./bulkupdaterequest/bulkupdaterequest.module').then(m => m.BulkUpdateRequestModule) },
 *     { path: 'organizations', loadChildren: () => import('./organization/organization.module').then(m => m.OrganizationModule) },
 */
class AuthGuard {
    constructor(authService, router) {
        this.authService = authService;
        this.router = router;
    }
    canActivate(route, state) {
        let checks = [];
        //siteAccess or single permission check.
        if (route.data && typeof route.data.auth === "string") {
            checks.push(this.authService.allowActivity(route.data.auth));
        }
        else {
            const auth = route.data.auth && new CanActivateAuth(route.data.auth);
            if (auth) {
                if (auth.all && auth.all.length > 0) {
                    checks.push(this.authService.allowAllActivities(auth.all));
                }
                if (auth.any && auth.any.length > 0) {
                    checks.push(this.authService.allowAnyActivity(auth.any));
                }
            }
        }
        return forkJoin(...checks).pipe(map((resp) => {
            if (!resp.every(x => x)) {
                this.router.navigate(['/forbidden']);
                return false;
            }
            return true;
        }));
    }
    static { this.ɵfac = function AuthGuard_Factory(t) { return new (t || AuthGuard)(i0.ɵɵinject(AuthorizationService), i0.ɵɵinject(i2.Router)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AuthGuard, factory: AuthGuard.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthGuard, [{
        type: Injectable
    }], () => [{ type: AuthorizationService }, { type: i2.Router }], null); })();

class AppHttpInterceptor {
    constructor(router, spinnerService, coreSettings) {
        this.router = router;
        this.spinnerService = spinnerService;
        this.coreSettings = coreSettings;
    }
    intercept(request, next) {
        request.headers.append('X-Requested-With', 'XMLHttpRequest');
        const disableSpinner = request.context.get(DISABLE_SPINNER)?.valueOf();
        const showSpinner = this.coreSettings.globalSpinner && !disableSpinner;
        if (showSpinner) {
            this.spinnerService.show();
        }
        return next.handle(request).pipe(map(res => res), catchError((err) => {
            if (err instanceof HttpErrorResponse) {
                if (this.handleError(err)) {
                    return throwError(err);
                }
                else {
                    return of(null);
                }
            }
            return throwError(err);
        }), finalize(() => {
            if (showSpinner) {
                this.spinnerService.hide();
            }
        }));
    }
    handleError(err) {
        if (err.status === 401) {
            let loc = err.headers.get('location');
            if (loc) {
                loc = loc.toLowerCase();
                if (loc.indexOf('connect/authorize') !== -1) {
                    window.location.reload();
                }
                else {
                    const returnUrlIndex = loc.indexOf('?returnurl=');
                    if (returnUrlIndex !== -1) {
                        const loginUrl = loc.substring(0, returnUrlIndex);
                        window.location.href = loginUrl + '?returnurl=' + encodeURIComponent(window.location.href);
                    }
                }
                return false;
            }
        }
        else if (err.status === 403) {
            this.router.navigate(["/forbidden"]);
            return false;
        }
        return true;
    }
    static { this.ɵfac = function AppHttpInterceptor_Factory(t) { return new (t || AppHttpInterceptor)(i0.ɵɵinject(i2.Router), i0.ɵɵinject(SpinnerService), i0.ɵɵinject('SurescriptsCoreModuleSettings')); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AppHttpInterceptor, factory: AppHttpInterceptor.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AppHttpInterceptor, [{
        type: Injectable
    }], () => [{ type: i2.Router }, { type: SpinnerService }, { type: undefined, decorators: [{
                type: Inject,
                args: ['SurescriptsCoreModuleSettings']
            }] }], null); })();
const DISABLE_SPINNER = new HttpContextToken(() => false);
class HttpHelpers {
    static spinnerDisabledGet(httpClient, url) {
        const httpContext = new HttpContext().set(DISABLE_SPINNER, true);
        const options = {
            context: httpContext
        };
        return httpClient.get(url, options);
    }
}

/**
 * Global Error handler. Will show a {@link ToastComponent} error message on error.
 */
class AppErrorHandler extends ErrorHandler {
    constructor(injector, ngZone) {
        super();
        this.injector = injector;
        this.ngZone = ngZone;
    }
    get toastService() {
        return this.injector.get(ToastService);
    }
    handleError(error) {
        let message = "An unexpected error has occurred.";
        if (error instanceof HttpErrorResponse) {
            if (error.status >= 400 && error.status <= 500) {
                message = error.error && error.error.message || message;
            }
        }
        this.ngZone.run(() => {
            this.toastService.error(message, { global: true });
            super.handleError(error);
        });
    }
    static { this.ɵfac = function AppErrorHandler_Factory(t) { return new (t || AppErrorHandler)(i0.ɵɵinject(Injector), i0.ɵɵinject(i0.NgZone)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AppErrorHandler, factory: AppErrorHandler.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AppErrorHandler, [{
        type: Injectable
    }], () => [{ type: i0.Injector, decorators: [{
                type: Inject,
                args: [Injector]
            }] }, { type: i0.NgZone }], null); })();

function SpinnerComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 1)(1, "div", 2)(2, "div", 3)(3, "span", 4);
    i0.ɵɵtext(4, "Loading...");
    i0.ɵɵelementEnd()();
    i0.ɵɵelement(5, "div", 3)(6, "div", 3)(7, "div", 3)(8, "div", 3)(9, "div", 3)(10, "div", 3)(11, "div", 3);
    i0.ɵɵelementEnd()();
} }
/**
 * Singleton Spinner component managed by the {@link SpinnerService}.
 *
 * Place the following html in your app.component.html.
 * @example
 * <surescripts-spinner></surescripts-spinner>
 */
class SpinnerComponent {
    constructor(spinnerService) {
        this.spinnerService = spinnerService;
    }
    static { this.ɵfac = function SpinnerComponent_Factory(t) { return new (t || SpinnerComponent)(i0.ɵɵdirectiveInject(SpinnerService)); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: SpinnerComponent, selectors: [["surescripts-spinner"]], decls: 1, vars: 1, consts: [["class", "spinner-container", 4, "ngIf"], [1, "spinner-container"], [1, "spinners"], ["role", "status", 1, "spinner-grow", "text-light"], [1, "sr-only"]], template: function SpinnerComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵtemplate(0, SpinnerComponent_div_0_Template, 12, 0, "div", 0);
        } if (rf & 2) {
            i0.ɵɵproperty("ngIf", ctx.spinnerService.isLoading);
        } }, dependencies: [i2$1.NgIf], styles: [".spinner-container[_ngcontent-%COMP%]{position:fixed;top:0;left:0;width:100%;height:100%;z-index:99999!important;background-color:#00000080}.spinner-container[_ngcontent-%COMP%] > div[_ngcontent-%COMP%]:not(.loading-text){top:50%;left:50%;margin:0;position:absolute;transform:translate(-50%,-50%)}.loading-text[_ngcontent-%COMP%]{position:absolute;top:60%;left:50%;transform:translate(-50%,-60%);z-index:99999!important}"] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SpinnerComponent, [{
        type: Component,
        args: [{ selector: 'surescripts-spinner', template: "<div class=\"spinner-container\" *ngIf=\"spinnerService.isLoading\">\n    <div class=\"spinners\">\n        <div class=\"spinner-grow text-light\" role=\"status\">\n            <span class=\"sr-only\">Loading...</span>\n        </div>\n        <div class=\"spinner-grow text-light\" role=\"status\"></div>\n        <div class=\"spinner-grow text-light\" role=\"status\"></div>\n        <div class=\"spinner-grow text-light\" role=\"status\"></div>\n        <div class=\"spinner-grow text-light\" role=\"status\"></div>\n        <div class=\"spinner-grow text-light\" role=\"status\"></div>\n        <div class=\"spinner-grow text-light\" role=\"status\"></div>\n        <div class=\"spinner-grow text-light\" role=\"status\"></div>\n    </div>\n</div>\n", styles: [".spinner-container{position:fixed;top:0;left:0;width:100%;height:100%;z-index:99999!important;background-color:#00000080}.spinner-container>div:not(.loading-text){top:50%;left:50%;margin:0;position:absolute;transform:translate(-50%,-50%)}.loading-text{position:absolute;top:60%;left:50%;transform:translate(-50%,-60%);z-index:99999!important}\n"] }]
    }], () => [{ type: SpinnerService }], null); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SpinnerComponent, { className: "SpinnerComponent", filePath: "lib/components/spinner/spinner.component.ts", lineNumber: 17 }); })();

function ToastComponent_ngb_toast_0_button_4_Template(rf, ctx) { if (rf & 1) {
    const _r5 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 5);
    i0.ɵɵlistener("click", function ToastComponent_ngb_toast_0_button_4_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r5); const toast_r1 = i0.ɵɵnextContext().$implicit; const ctx_r3 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r3.toastService.remove(toast_r1)); });
    i0.ɵɵelementEnd();
} }
function ToastComponent_ngb_toast_0_Template(rf, ctx) { if (rf & 1) {
    const _r7 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "ngb-toast", 1);
    i0.ɵɵlistener("mouseenter", function ToastComponent_ngb_toast_0_Template_ngb_toast_mouseenter_0_listener() { i0.ɵɵrestoreView(_r7); const ctx_r6 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r6.autohide = false); })("mouseleave", function ToastComponent_ngb_toast_0_Template_ngb_toast_mouseleave_0_listener() { i0.ɵɵrestoreView(_r7); const ctx_r8 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r8.autohide = true); })("hide", function ToastComponent_ngb_toast_0_Template_ngb_toast_hide_0_listener() { const restoredCtx = i0.ɵɵrestoreView(_r7); const toast_r1 = restoredCtx.$implicit; const ctx_r9 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r9.toastService.remove(toast_r1)); });
    i0.ɵɵelementStart(1, "div", 2)(2, "div", 3);
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, ToastComponent_ngb_toast_0_button_4_Template, 1, 0, "button", 4);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const toast_r1 = ctx.$implicit;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵclassMap(toast_r1.classname);
    i0.ɵɵproperty("delay", toast_r1.delay)("autohide", ctx_r0.autohide)("header", toast_r1.header);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(toast_r1.text);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !toast_r1.header);
} }
/**
 * Singleton Toast component managed by the {@link ToastService}.
 *
 * Place the following html in your app.component.html.
 * @example
 * <surescripts-toasts aria-live="polite" aria-atomic="true" class="ss-toasts"></surescripts-toasts>
 */
class ToastComponent {
    constructor(toastService) {
        this.toastService = toastService;
        this.show = false;
        this.autohide = true;
    }
    isTemplate(toast) { return toast.textOrTpl instanceof TemplateRef; }
    static { this.ɵfac = function ToastComponent_Factory(t) { return new (t || ToastComponent)(i0.ɵɵdirectiveInject(ToastService)); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ToastComponent, selectors: [["surescripts-toasts"]], decls: 1, vars: 1, consts: [[3, "class", "delay", "autohide", "header", "mouseenter", "mouseleave", "hide", 4, "ngFor", "ngForOf"], [3, "delay", "autohide", "header", "mouseenter", "mouseleave", "hide"], [1, "d-flex", "justify-content-between"], [1, "text", "text-break"], ["aria-label", "Close", "class", "btn-close", "type", "button", 3, "click", 4, "ngIf"], ["aria-label", "Close", "type", "button", 1, "btn-close", 3, "click"]], template: function ToastComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵtemplate(0, ToastComponent_ngb_toast_0_Template, 5, 7, "ngb-toast", 0);
        } if (rf & 2) {
            i0.ɵɵproperty("ngForOf", ctx.toastService.toasts);
        } }, dependencies: [i2$1.NgForOf, i2$1.NgIf, i1$1.NgbToast], styles: ["[_nghost-%COMP%]{position:fixed;bottom:0!important;right:0!important;z-index:10000;width:max-content;max-width:100%;pointer-events:none;padding:1rem}ngb-toast[_ngcontent-%COMP%]{width:350px;max-width:100%;font-size:.875rem;pointer-events:auto}"] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ToastComponent, [{
        type: Component,
        args: [{ selector: 'surescripts-toasts', template: "<ngb-toast *ngFor=\"let toast of toastService.toasts\"\n           [class]=\"toast.classname\"\n           [delay]=\"toast.delay\"\n           [autohide]=\"autohide\"\n           (mouseenter)=\"autohide = false\"\n           (mouseleave)=\"autohide = true\"\n           (hide)=\"toastService.remove(toast)\"\n           [header]=\"toast.header\">\n    <div class=\"d-flex justify-content-between\">\n        <div class=\"text text-break\">{{ toast.text }}</div>\n        <button aria-label=\"Close\" *ngIf=\"!toast.header\" class=\"btn-close\" (click)=\"toastService.remove(toast)\" type=\"button\"></button>\n    </div>\n</ngb-toast>\n", styles: [":host{position:fixed;bottom:0!important;right:0!important;z-index:10000;width:max-content;max-width:100%;pointer-events:none;padding:1rem}ngb-toast{width:350px;max-width:100%;font-size:.875rem;pointer-events:auto}\n"] }]
    }], () => [{ type: ToastService }], null); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(ToastComponent, { className: "ToastComponent", filePath: "lib/components/toast/toast.component.ts", lineNumber: 16 }); })();

const _c0$2 = () => ["fas", "exclamation-triangle"];
class ForbiddenComponent {
    constructor(leftNavService) {
        this.leftNavService = leftNavService;
    }
    ngOnInit() {
        this.leftNavService.init();
    }
    static { this.ɵfac = function ForbiddenComponent_Factory(t) { return new (t || ForbiddenComponent)(i0.ɵɵdirectiveInject(i1$2.LeftNavigationService)); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ForbiddenComponent, selectors: [["app-forbidden"]], decls: 7, vars: 2, consts: [[1, "container", 2, "font-size", "30px"], [1, "text-center", "fa-10x", "mb-1"], [1, "fa-fw"], [3, "icon"], ["content", "Access\u00A0Denied", "transform", "shrink-14.2 down-6", 1, "fa-inverse"], [1, "text-center"]], template: function ForbiddenComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0)(1, "h1", 1)(2, "fa-layers", 2);
            i0.ɵɵelement(3, "fa-icon", 3)(4, "fa-layers-text", 4);
            i0.ɵɵelementEnd()();
            i0.ɵɵelementStart(5, "h3", 5);
            i0.ɵɵtext(6, "You don't have permission to access this page.");
            i0.ɵɵelementEnd()();
        } if (rf & 2) {
            i0.ɵɵadvance(3);
            i0.ɵɵproperty("icon", i0.ɵɵpureFunction0(1, _c0$2));
        } }, dependencies: [i2$2.FaIconComponent, i2$2.FaLayersComponent, i2$2.FaLayersTextComponent], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ForbiddenComponent, [{
        type: Component,
        args: [{ selector: 'app-forbidden', template: "<div class=\"container\" style=\"font-size: 30px\">\n  <h1 class=\"text-center fa-10x mb-1\">\n    <fa-layers class=\"fa-fw\">\n      <fa-icon [icon]=\"['fas', 'exclamation-triangle']\"></fa-icon>\n      <fa-layers-text content=\"Access&nbsp;Denied\" class=\"fa-inverse\" transform=\"shrink-14.2 down-6\"></fa-layers-text>\n    </fa-layers>\n  </h1>\n  <h3 class=\"text-center\">You don't have permission to access this page.</h3>\n</div>\n" }]
    }], () => [{ type: i1$2.LeftNavigationService }], null); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(ForbiddenComponent, { className: "ForbiddenComponent", filePath: "lib/components/forbidden/forbidden.component.ts", lineNumber: 8 }); })();

class ModalConfirmContent {
    constructor(init) {
        this.header = "Confirm";
        this.okButtonText = "Yes";
        this.cancelButtonText = "No";
        Object.assign(this, init);
    }
}

var ModalResult;
(function (ModalResult) {
    ModalResult[ModalResult["Cancel"] = 0] = "Cancel";
    ModalResult[ModalResult["Ok"] = 1] = "Ok";
})(ModalResult || (ModalResult = {}));

const _c0$1 = ["bodyComponentContainer"];
function ModalConfirmComponent_p_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "p");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.content.secondaryBody);
} }
function ModalConfirmComponent_button_11_Template(rf, ctx) { if (rf & 1) {
    const _r5 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 9);
    i0.ɵɵlistener("click", function ModalConfirmComponent_button_11_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r5); const ctx_r4 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r4.ok()); });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r2.content.okButtonText);
} }
function ModalConfirmComponent_button_12_Template(rf, ctx) { if (rf & 1) {
    const _r7 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 10);
    i0.ɵɵlistener("click", function ModalConfirmComponent_button_12_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r7); const ctx_r6 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r6.cancel()); });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r3.content.cancelButtonText);
} }
/**
 * Standard Modal confirm with the ability to inject additional content.
 * @example
 * const modalRef = this.modalService.openConfirm({ body: "Body" });
 * modalRef.result.then((resp: ModalResult) => {
 *      if(resp === ModalResult.Ok) {
 *          //...
 *      }
 * });
 * //To inject additional information.
 * //modalRef.componentInstance.loadBodyComponent(AdditionalContent);
 */
class ModalConfirmComponent {
    constructor(activeModal, componentFactoryResolver) {
        this.activeModal = activeModal;
        this.componentFactoryResolver = componentFactoryResolver;
        this.content = new ModalConfirmContent();
    }
    ok() {
        this.activeModal.close(ModalResult.Ok);
    }
    cancel() {
        this.activeModal.close(ModalResult.Cancel);
    }
    setContent(content) {
        this.content = Object.assign(this.content, content);
    }
    loadBodyComponent(bodyComponentType) {
        let factory = this.componentFactoryResolver.resolveComponentFactory(bodyComponentType);
        this.bodyComponentRef = this.container.createComponent(factory);
        return this.bodyComponentRef.instance;
    }
    ngOnDestroy() {
        if (this.bodyComponentRef) {
            this.bodyComponentRef.destroy();
        }
    }
    static { this.ɵfac = function ModalConfirmComponent_Factory(t) { return new (t || ModalConfirmComponent)(i0.ɵɵdirectiveInject(i1$1.NgbActiveModal), i0.ɵɵdirectiveInject(i0.ComponentFactoryResolver)); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ModalConfirmComponent, selectors: [["surescripts-modal-content"]], viewQuery: function ModalConfirmComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$1, 7, ViewContainerRef);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.container = _t.first);
        } }, decls: 13, vars: 5, consts: [[1, "modal-header"], [1, "modal-title"], ["type", "button", "aria-label", "Close", 1, "btn-close", 3, "click"], [1, "modal-body"], [4, "ngIf"], ["bodyComponentContainer", ""], [1, "modal-footer"], ["type", "button", "class", "btn btn-primary", 3, "click", 4, "ngIf"], ["type", "button", "class", "btn btn-secondary", 3, "click", 4, "ngIf"], ["type", "button", 1, "btn", "btn-primary", 3, "click"], ["type", "button", 1, "btn", "btn-secondary", 3, "click"]], template: function ModalConfirmComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0)(1, "h4", 1);
            i0.ɵɵtext(2);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(3, "button", 2);
            i0.ɵɵlistener("click", function ModalConfirmComponent_Template_button_click_3_listener() { return ctx.cancel(); });
            i0.ɵɵelementEnd()();
            i0.ɵɵelementStart(4, "div", 3)(5, "p");
            i0.ɵɵtext(6);
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(7, ModalConfirmComponent_p_7_Template, 2, 1, "p", 4);
            i0.ɵɵelement(8, "template", null, 5);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(10, "div", 6);
            i0.ɵɵtemplate(11, ModalConfirmComponent_button_11_Template, 2, 1, "button", 7)(12, ModalConfirmComponent_button_12_Template, 2, 1, "button", 8);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵadvance(2);
            i0.ɵɵtextInterpolate(ctx.content.header);
            i0.ɵɵadvance(4);
            i0.ɵɵtextInterpolate(ctx.content.body);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.content.secondaryBody);
            i0.ɵɵadvance(4);
            i0.ɵɵproperty("ngIf", ctx.content.okButtonText);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.content.cancelButtonText);
        } }, dependencies: [i2$1.NgIf], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ModalConfirmComponent, [{
        type: Component,
        args: [{ selector: 'surescripts-modal-content', template: "<div class=\"modal-header\">\n    <h4 class=\"modal-title\">{{content.header}}</h4>\n    <button type=\"button\" class=\"btn-close\" aria-label=\"Close\" (click)=\"cancel()\"></button>\t\n</div>\n<div class=\"modal-body\">\n    <p>{{content.body}}</p>\n    <p *ngIf=\"content.secondaryBody\">{{content.secondaryBody}}</p>\n    <template #bodyComponentContainer></template>\n</div>\n<div class=\"modal-footer\">\n  <button *ngIf=\"content.okButtonText\" type=\"button\" class=\"btn btn-primary\" (click)=\"ok()\">{{content.okButtonText}}</button>\n  <button *ngIf=\"content.cancelButtonText\" type=\"button\" class=\"btn btn-secondary\" (click)=\"cancel()\">{{content.cancelButtonText}}</button>\n</div>\n" }]
    }], () => [{ type: i1$1.NgbActiveModal }, { type: i0.ComponentFactoryResolver }], { container: [{
            type: ViewChild,
            args: ["bodyComponentContainer", { static: true, read: ViewContainerRef }]
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(ModalConfirmComponent, { className: "ModalConfirmComponent", filePath: "lib/components/modal/modal-confirm.component.ts", lineNumber: 25 }); })();

/**
 * Global service for managing modals. This wraps [NgbModal](https://ng-bootstrap.github.io/releases/11.x/#/components/modal/api#NgbModal) and will treat dismiss as cancels so that users dont need to catch the rejected promise.
 */
class ModalService {
    constructor(modal) {
        this.modal = modal;
    }
    /**
     * Open up a standard Modal Confirm window.
     * @param confirmContent used for setting specific portions of the {@link ModalConfirmComponent}.
     * @param options ng-bootstrap options. [NgbModalOptions](https://ng-bootstrap.github.io/releases/11.x/#/components/modal/api#NgbModalOptions)
     */
    openConfirm(confirmContent, options) {
        const modalRef = this.openContent(ModalConfirmComponent, options);
        const confirm = modalRef.componentInstance;
        confirm.setContent(confirmContent);
        return modalRef;
    }
    /**
     * Open up a custom Modal window for anything non standard.
     * @param content Any custom component alias that you want to display.
     * @param options ng-bootstrap options. [NgbModalOptions](https://ng-bootstrap.github.io/releases/11.x/#/components/modal/api#NgbModalOptions)
     */
    openContent(content, options) {
        const modalRef = this.modal.open(content, options);
        //Catch the rejected promise for dismiss.
        modalRef.result = from(modalRef.result).pipe(catchError(err => of(ModalResult.Cancel))).toPromise();
        return modalRef;
    }
    static forRoot() {
        return {
            ngModule: ModalService,
            providers: []
        };
    }
    static { this.ɵfac = function ModalService_Factory(t) { return new (t || ModalService)(i0.ɵɵinject(i1$1.NgbModal)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ModalService, factory: ModalService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ModalService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], () => [{ type: i1$1.NgbModal }], null); })();

class LinkingService {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    getBaseUrl(dataUrl, clientId, permissions) {
        let url = `${dataUrl}/${clientId}`;
        if (permissions && permissions.length > 0) {
            url = `${url}?${permissions.map(x => "permission=" + x).join('&')}`;
        }
        return this.httpClient.get(url, { responseType: "text" });
    }
    static { this.ɵfac = function LinkingService_Factory(t) { return new (t || LinkingService)(i0.ɵɵinject(i1.HttpClient)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: LinkingService, factory: LinkingService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LinkingService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], () => [{ type: i1.HttpClient }], null); })();

/**
 * Linking Directive used for rendering considitional links between microsites based on permissions.
 *
 * @example
 * <div>
      <a ssoLink="user-service" href="foo/bar" class="foo">Link To User Service</a>
   </div>
   <div>
      <a ssoLink="user-service" (onHasAccess)="onHasAccess($event)">Link without relative path</a>
   </div>
   <div>
      <a ssoLink="user-service" [permissions]="['asdf','dfdfdfdfd']">Link with multiple permissions</a>
   </div>
    <div>
      <a ssoLink="user-service" permissions="onepermission">Link with one permission</a>
    </div>
    <div>
      Link with no Access<a ssoLink="user-service" permissions="nope">no access</a>
    </div>
 */
class LinkingDirective {
    get hasAccess() {
        return !this._hasAccess;
    }
    constructor(linkService) {
        this.linkService = linkService;
        this.permissions = [];
        /**
         * dataUrl is the api that your application will call to generate the link based on permissions and clientId provided. Defaults to /shared/link and is dependent on the Web.Core proxy libraries.
         */
        this.dataUrl = "/shared/link";
        this.onHasAccess = new EventEmitter();
        /**
         * href will hold the path to the feature you are linking to.
         */
        this.href = '';
        this._hasAccess = false;
    }
    ngOnInit() {
        if (!this.dataUrl) {
            this.dataUrl = "/shared/link";
        }
        if (!this.ssoLink) {
            throw new Error("ssoLink attribute requires a valid clientId value.");
        }
        if (this.permissions && !Array.isArray(this.permissions)) {
            this.permissions = [this.permissions];
        }
        this.permissions = this.permissions ?? [];
        this.linkService.getBaseUrl(this.dataUrl, this.ssoLink, this.permissions).pipe(tap((baseUrl) => {
            if (baseUrl) {
                if (!baseUrl.endsWith('/')) {
                    baseUrl = baseUrl + '/';
                }
                if (this.href.startsWith("/")) {
                    this.href = this.href.substring(1);
                }
                this.href = `${baseUrl}${this.href ? this.href : ''}`;
                this._hasAccess = true;
            }
        }), catchError(e => {
            return of();
        }), finalize(() => {
            this.onHasAccess.emit(this._hasAccess);
        })).subscribe();
    }
    static { this.ɵfac = function LinkingDirective_Factory(t) { return new (t || LinkingDirective)(i0.ɵɵdirectiveInject(LinkingService)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: LinkingDirective, selectors: [["a", "ssoLink", ""]], hostVars: 3, hostBindings: function LinkingDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵhostProperty("href", ctx.href, i0.ɵɵsanitizeUrl);
            i0.ɵɵclassProp("d-none", ctx.hasAccess);
        } }, inputs: { ssoLink: "ssoLink", permissions: "permissions", dataUrl: "dataUrl", href: "href" }, outputs: { onHasAccess: "onHasAccess" } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LinkingDirective, [{
        type: Directive,
        args: [{
                selector: 'a[ssoLink]'
            }]
    }], () => [{ type: LinkingService }], { ssoLink: [{
            type: Input
        }], permissions: [{
            type: Input
        }], dataUrl: [{
            type: Input
        }], onHasAccess: [{
            type: Output
        }], href: [{
            type: Input
        }, {
            type: HostBinding,
            args: ['href']
        }], hasAccess: [{
            type: HostBinding,
            args: ['class.d-none']
        }] }); })();

class SessionTimeoutOptions {
    constructor(init) {
        /**
         * Controls how soon (in milliseconds) before the timeout modal will display prior to the session ending.
         * Default is one minute (60000ms)
         * @default 60000
         */
        this.modalLeadTime = 60000;
        /**
         * When a user is active, this is the minimum amount of time (in milliseconds) between calls to
         * extend the user's session. This prevents excess requests to extend the backend session.
         * Default is one minute (60000ms)
         * @default 60000
         * */
        this.sessionExtendFrequency = 60000;
        /**
         * Url to call to check the status of the backend session. Expects return value to contain the session expiration time.
         * @default 'sso-session/check'
         */
        this.checkSessionEndpoint = 'sso-session/check';
        /**
         * Url to call to extend backend session. A "fire and forget" GET request will be made to this endpoint while a user is active.
         * @default 'sso-session/extend'
         */
        this.extendSessionEndpoint = 'sso-session/extend';
        /**
         * Url to call to extend backend session. A "fire and forget" GET request will be made to this endpoint to terminate a session.
         * @default 'sso-session/end'
         */
        this.endSessionEndpoint = `sso-session/end`;
        /**
         * Url the browser will be directed to after a session has been terminated.
         * If this value is null, the browser will reload the current url to force backend session handling.
         * @default null
         */
        this.ssoExpiredSessionLocation = null;
        /**
         * If set, this is the text that will display as the application name in the Session Timeout Modal window.
         */
        this.appDisplayName = null;
        Object.assign(this, init);
    }
}

const _c0 = () => ["far", "clock"];
class SessionTimeoutModalComponent {
    constructor(activeModal) {
        this.activeModal = activeModal;
        this._secondsLeft = 0;
        this._appName = null;
    }
    get secondsLeft() {
        return this._secondsLeft;
    }
    set secondsLeft(value) {
        this._secondsLeft = value;
    }
    set appDisplayName(value) {
        this._appName = value;
    }
    get appDisplayName() {
        return this._appName ?? "The application";
    }
    extend() {
        this.activeModal.close(ModalResult.Ok);
    }
    end() {
        this.activeModal.close(ModalResult.Cancel);
    }
    static { this.ɵfac = function SessionTimeoutModalComponent_Factory(t) { return new (t || SessionTimeoutModalComponent)(i0.ɵɵdirectiveInject(i1$1.NgbActiveModal)); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: SessionTimeoutModalComponent, selectors: [["session-timeout-modal"]], decls: 18, vars: 4, consts: [[1, "modal-header"], [1, "modal-title"], [1, "modal-body", "container-fluid"], [1, "row", "align-items-center", "h-100"], [1, "text-center", "text-muted", "col-2", "fa-5x"], [3, "icon"], [1, "col", "h-100"], [1, "mb-0"], [1, "modal-footer"], ["type", "button", 1, "btn", "btn-primary", 3, "click"], ["type", "button", 1, "btn", "btn-secondary", 3, "click"]], template: function SessionTimeoutModalComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0)(1, "h4", 1);
            i0.ɵɵtext(2, "Session Timeout");
            i0.ɵɵelementEnd()();
            i0.ɵɵelementStart(3, "div", 2)(4, "div", 3)(5, "div", 4);
            i0.ɵɵelement(6, "fa-icon", 5);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(7, "div", 6)(8, "p", 7);
            i0.ɵɵtext(9);
            i0.ɵɵelementStart(10, "strong");
            i0.ɵɵtext(11);
            i0.ɵɵelementEnd();
            i0.ɵɵtext(12, ". Any unsaved changes will be lost. You can either continue working or end your session.");
            i0.ɵɵelementEnd()()()();
            i0.ɵɵelementStart(13, "div", 8)(14, "button", 9);
            i0.ɵɵlistener("click", function SessionTimeoutModalComponent_Template_button_click_14_listener() { return ctx.extend(); });
            i0.ɵɵtext(15, "Continue Session");
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(16, "button", 10);
            i0.ɵɵlistener("click", function SessionTimeoutModalComponent_Template_button_click_16_listener() { return ctx.end(); });
            i0.ɵɵtext(17, "End Session");
            i0.ɵɵelementEnd()();
        } if (rf & 2) {
            i0.ɵɵadvance(6);
            i0.ɵɵproperty("icon", i0.ɵɵpureFunction0(3, _c0));
            i0.ɵɵadvance(3);
            i0.ɵɵtextInterpolate1("", ctx.appDisplayName, " will timeout in ");
            i0.ɵɵadvance(2);
            i0.ɵɵtextInterpolate1("", ctx.secondsLeft, " seconds");
        } }, dependencies: [i2$2.FaIconComponent], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SessionTimeoutModalComponent, [{
        type: Component,
        args: [{ selector: 'session-timeout-modal', template: "<div class=\"modal-header\">\n    <h4 class=\"modal-title\">Session Timeout</h4>\n</div>\n<div class=\"modal-body container-fluid\">\n  <div class=\"row align-items-center h-100\">\n    <div class=\"text-center text-muted col-2 fa-5x\">\n      <fa-icon [icon]=\"['far', 'clock']\"></fa-icon>\n    </div>\n    <div class=\"col h-100\">\n      <p class=\"mb-0\">{{ appDisplayName }} will timeout in <strong>{{ secondsLeft }} seconds</strong>. Any unsaved changes will be lost. You can either continue working or end your session.</p>\n    </div>\n  </div>\n</div>\n<div class=\"modal-footer\">\n  <button type=\"button\" class=\"btn btn-primary\" (click)=\"extend()\">Continue Session</button>\n  <button type=\"button\" class=\"btn btn-secondary\" (click)=\"end()\">End Session</button>\n</div>\n" }]
    }], () => [{ type: i1$1.NgbActiveModal }], null); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SessionTimeoutModalComponent, { className: "SessionTimeoutModalComponent", filePath: "lib/components/session-timeout/session-timeout-modal.component.ts", lineNumber: 9 }); })();

/**
 * Service responsible for tracking user activity and displaying a modal after the specified
 * period of inactivity. The service will either extend or end the session appropriately based
 * on user activity and user response to modal.
 *
 * @example
 * this.timeoutService.start().subscribe(() => {
 *     // This observable is fired when the session is about to be terminated
 * });
 *
 * // Shut down service and no longer monitor user activity. Sessions will not be extended.
 * this.timeoutService.stop()
 */
class SessionTimeoutService {
    constructor(http, modal, router, modalService, titleService) {
        this.http = http;
        this.modal = modal;
        this.router = router;
        this.modalService = modalService;
        this.titleService = titleService;
        this.isStarted = false;
        this.previousPageTitle = "";
        this.expired$ = new Subject();
    }
    ngOnDestroy() {
        this.stop();
    }
    /**
     * Start monitoring the user's activity. While the user is active, the service will make requests to extend the
     * server-side session. Once the service is started it will be responsible for displaying the modal timeout warning,
     * extending and ending sessions, updating page title with countdown timer, and redirecting to the appropriate page
     * when a session is terminated.
     *
     * @returns {Observable<any>} Observable that is triggered when a session is being terminated.
     */
    start(options) {
        if (this.isStarted) {
            return this.expired$;
        }
        this.options = options ?? new SessionTimeoutOptions();
        this.isStarted = true;
        this.checkSession().pipe(take(1), tap(() => {
            this.startMonitoringActivity();
            this.startSessionTimer();
        })).subscribe();
        return this.expired$;
    }
    /**
     * Stops all functions of the service.
     */
    stop() {
        if (!this.isStarted) {
            return;
        }
        this.stopMonitoringActivity();
        this.stopSessionTimer();
        this.isStarted = false;
    }
    startMonitoringActivity() {
        this.userActivity$ = merge(fromEvent(document, 'click'), fromEvent(document, 'mousedown'), fromEvent(document, 'keypress'), fromEvent(document, 'DOMMouseScroll'), fromEvent(document, 'mousewheel'));
        this.userActivitySubscription = this.userActivity$.pipe(throttleTime(this.options.sessionExtendFrequency), switchMap(() => this.extendSession())).subscribe();
    }
    startSessionTimer() {
        const sessionTTL = this.sessionExpirationMilliseconds - Date.now();
        const promptTime = sessionTTL - this.options.modalLeadTime;
        this.sessionTimer$ = timer(promptTime).pipe(take(1), switchMap(() => this.checkSession()), tap(resp => {
            // Display the modal if the session has not been extended elsewhere (other tab/window).
            if ((this.sessionExpirationMilliseconds - this.options.modalLeadTime) <= Date.now()) {
                this.promptForTimeout();
            }
            else {
                this.stopSessionTimer();
                this.startSessionTimer();
            }
        })).subscribe();
    }
    stopSessionTimer() {
        this.sessionTimer$.unsubscribe();
        if (this.modalCountdownTimer$) {
            this.modalCountdownTimer$.unsubscribe();
        }
    }
    checkSession() {
        return HttpHelpers.spinnerDisabledGet(this.http, this.options.checkSessionEndpoint)
            .pipe(tap(resp => {
            this.handleCheckSessionResponse(resp);
        }), catchError((err, caught) => {
            return of(new CheckSessionResponse());
        }));
    }
    extendSession() {
        this.stopSessionTimer();
        return HttpHelpers.spinnerDisabledGet(this.http, this.options.extendSessionEndpoint).pipe(tap(resp => {
            this.handleCheckSessionResponse(resp);
            this.startSessionTimer();
        }));
    }
    handleCheckSessionResponse(resp) {
        if (!resp.isSessionValid || Date.now() > resp.sessionExpiresMilliseconds) {
            this.endSession();
        }
        else {
            this.sessionExpirationMilliseconds = resp.sessionExpiresMilliseconds;
        }
    }
    promptForTimeout() {
        this.stopMonitoringActivity();
        this.stopSessionTimer();
        const modalOptions = {
            backdrop: 'static'
        };
        // Show Modal
        this.previousPageTitle = this.titleService.getTitle();
        const modalRef = this.modalService.openContent(SessionTimeoutModalComponent, modalOptions);
        const componentRef = modalRef.componentInstance;
        componentRef.appDisplayName = this.options.appDisplayName;
        modalRef.result.then((result) => {
            if (result === ModalResult.Ok) {
                this.modalCountdownTimer$.unsubscribe();
                this.titleService.setTitle(this.previousPageTitle);
                this.checkSession().pipe(take(1), switchMap(() => this.extendSession()), tap(() => {
                    this.startMonitoringActivity();
                })).subscribe();
            }
            else if (result == ModalResult.Cancel) {
                this.endSession(true);
            }
        });
        // Set Modal Countdown Timer to update remaining time in title and modal body.
        this.modalCountdownTimer$ = timer(0, 1000).pipe(map(() => {
            const secondsLeft = Math.ceil((this.sessionExpirationMilliseconds - Date.now()) / 1000);
            // Prevent any negative numbers while we wait for end session activities
            if (secondsLeft >= 0) {
                this.titleService.setTitle(`(${secondsLeft}s) ${this.previousPageTitle}`);
                componentRef.secondsLeft = secondsLeft;
            }
            return secondsLeft;
        }), concatMap(secondsLeft => {
            if (secondsLeft <= 0 || (secondsLeft % 5 === 0)) {
                return this.checkSession();
            }
            return of(null);
        }), tap((resp) => {
            if (!resp)
                return;
            const sessionTTL = this.sessionExpirationMilliseconds - Date.now();
            if (sessionTTL > this.options.modalLeadTime) {
                this.titleService.setTitle(this.previousPageTitle);
                modalRef.close();
                this.stopSessionTimer();
                this.startSessionTimer();
            }
        })).subscribe();
    }
    stopMonitoringActivity() {
        this.userActivitySubscription.unsubscribe();
    }
    endSession(endBackendSession = false) {
        this.stop();
        this.expired$.next(null);
        this.expired$.complete();
        if (endBackendSession) {
            HttpHelpers.spinnerDisabledGet(this.http, this.options.endSessionEndpoint).subscribe(() => {
                this.redirectToExpiredLocation();
            });
        }
        else {
            this.redirectToExpiredLocation();
        }
    }
    redirectToExpiredLocation() {
        const currentUrl = window.location.href;
        window.location.href = this.options.ssoExpiredSessionLocation ?
            `${this.options.ssoExpiredSessionLocation.replace(/\/+$/, '')}?returnUrl=${encodeURIComponent(currentUrl)}` :
            currentUrl;
    }
    static { this.ɵfac = function SessionTimeoutService_Factory(t) { return new (t || SessionTimeoutService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(ModalService), i0.ɵɵinject(i2.Router), i0.ɵɵinject(ModalService), i0.ɵɵinject(i4.Title)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: SessionTimeoutService, factory: SessionTimeoutService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SessionTimeoutService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], () => [{ type: i1.HttpClient }, { type: ModalService }, { type: i2.Router }, { type: ModalService }, { type: i4.Title }], null); })();
class CheckSessionResponse {
}

class User {
    constructor(init) {
        Object.assign(this, init);
    }
}

/**
 * Extendable deactivate component that makes use of a default `canDeactivateGuard` and includes support for a confirm modal when a user attempts to navigate away from the route.
 *
 * Apply the guard on a route.
 * @example
 * { path: 'search', component: SearchComponent, canDeactivate: [canDeactivateGuard] },
 *
 * Extend `DefaultDeactivateComponent` on your target component.
 * @example
 *  @Component({
 *    selector: 'app-search',
 *    templateUrl: './search.component.html'
 * })
 * export class SearchComponent extends DefaultDeactivateComponent {
 *    deactivateModalContent = { body: 'You have not submitted your form. Are you sure you want to leave this page?' };
 *    deactivateModalOptions = { centered: true };
 *    modalService: ModalService;
 *    constructor(modalService: ModalService) {
 *        super();
 *        this.modalService = modalService;
 *    }
 *    // rest of your code...
 * }
 *
 * You can also set your own custom `canDeactivate()` behavior by implementing the `ComponentCanDeactivate` interface on your own component instead of extending `DefaultDeactivateComponent`.
 */
class DefaultDeactivateComponent {
    canDeactivate() {
        const modalRef = this.modalService.openConfirm(this.deactivateModalContent, this.deactivateModalOptions);
        return modalRef.result.then((result) => {
            return result === ModalResult.Ok;
        });
    }
}
const canDeactivateGuard = (component, currentRoute, currentState, nextState) => {
    return nextState.url?.includes('sessionexpired') ? true : (component.canDeactivate ? component.canDeactivate() : true);
};

// @dynamic
class SurescriptsCoreModule {
    constructor(fontLibrary) {
        fontLibrary.addIcons(faTimes, faExclamationTriangle, faClock);
    }
    static forRoot(settings) {
        return {
            ngModule: SurescriptsCoreModule,
            providers: [
                ToastService,
                SpinnerService,
                AuthGuard,
                CachingService,
                ModalService,
                AuthorizationService,
                LinkingService,
                SessionTimeoutService,
                { provide: 'SurescriptsCoreModuleSettings', useValue: (settings || { 'globalSpinner': true })
                }
            ]
        };
    }
    static { this.ɵfac = function SurescriptsCoreModule_Factory(t) { return new (t || SurescriptsCoreModule)(i0.ɵɵinject(i2$2.FaIconLibrary)); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: SurescriptsCoreModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [
            ToastService,
            SpinnerService,
            AuthGuard,
            CachingService,
            AuthorizationService,
            LinkingService,
            ModalService,
            SessionTimeoutService,
            {
                provide: APP_INITIALIZER, useFactory: (authorizationService) => () => {
                    return forkJoin([authorizationService.getUser(), authorizationService.getUserPermissions()]).toPromise();
                }, deps: [AuthorizationService], multi: true
            },
            { provide: ErrorHandler, useClass: AppErrorHandler },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: AppHttpInterceptor,
                multi: true
            },
            {
                provide: 'SurescriptsCoreModuleSettings', useValue: { 'globalSpinner': true }
            }
        ], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            FontAwesomeModule,
            NgbModule, CommonModule,
            FormsModule,
            ReactiveFormsModule,
            FontAwesomeModule,
            NgbModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SurescriptsCoreModule, [{
        type: NgModule,
        args: [{
                declarations: [SpinnerComponent, ToastComponent, ForbiddenComponent, ModalConfirmComponent, LinkingDirective, SessionTimeoutModalComponent],
                providers: [
                    ToastService,
                    SpinnerService,
                    AuthGuard,
                    CachingService,
                    AuthorizationService,
                    LinkingService,
                    ModalService,
                    SessionTimeoutService,
                    {
                        provide: APP_INITIALIZER, useFactory: (authorizationService) => () => {
                            return forkJoin([authorizationService.getUser(), authorizationService.getUserPermissions()]).toPromise();
                        }, deps: [AuthorizationService], multi: true
                    },
                    { provide: ErrorHandler, useClass: AppErrorHandler },
                    {
                        provide: HTTP_INTERCEPTORS,
                        useClass: AppHttpInterceptor,
                        multi: true
                    },
                    {
                        provide: 'SurescriptsCoreModuleSettings', useValue: { 'globalSpinner': true }
                    }
                ],
                imports: [
                    CommonModule,
                    FormsModule,
                    ReactiveFormsModule,
                    FontAwesomeModule,
                    NgbModule
                ],
                exports: [
                    CommonModule,
                    FormsModule,
                    ReactiveFormsModule,
                    FontAwesomeModule,
                    NgbModule,
                    SpinnerComponent,
                    ToastComponent,
                    LinkingDirective
                ]
            }]
    }], () => [{ type: i2$2.FaIconLibrary }], null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SurescriptsCoreModule, { declarations: [SpinnerComponent, ToastComponent, ForbiddenComponent, ModalConfirmComponent, LinkingDirective, SessionTimeoutModalComponent], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        FontAwesomeModule,
        NgbModule], exports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        FontAwesomeModule,
        NgbModule,
        SpinnerComponent,
        ToastComponent,
        LinkingDirective] }); })();

/*
 * Public API Surface of core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AppErrorHandler, AppHttpInterceptor, AuthGuard, AuthorizationService, CachingService, CanActivateAuth, DISABLE_SPINNER, DefaultDeactivateComponent, ForbiddenComponent, HttpHelpers, LinkingDirective, LinkingService, ModalConfirmComponent, ModalConfirmContent, ModalResult, ModalService, SessionTimeoutModalComponent, SessionTimeoutOptions, SessionTimeoutService, SpinnerComponent, SpinnerService, SurescriptsCoreModule, ToastComponent, ToastService, User, canDeactivateGuard };
